import { useState, useCallback } from "react";
import {
    listBoardsApi,
    createBoardApi,
    updateBoardApi,
    deleteBoardApi,
    getBoardTasksApi,
    getBoardStatusesApi
} from "../services/boardsService";

export function useBoards({ token, workspaceId }) {
    const [boards, setBoards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const fetchBoards = useCallback(async () => {
        if (!workspaceId) return;
        setLoading(true);
        try {
            const data = await listBoardsApi({ token, workspaceId });
            setBoards(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
            setError("Failed to load boards");
        } finally {
            setLoading(false);
        }
    }, [token, workspaceId]);

    const createBoard = async (data) => {
        try {
            const newBoard = await createBoardApi({ token, workspaceId, data });
            setBoards(prev => [newBoard, ...prev]);
            return newBoard;
        } catch (err) {
            console.error(err);
            throw err;
        }
    };

    const updateBoard = async (boardId, data) => {
        setBoards(prev => prev.map(b => b.id === boardId ? { ...b, ...data } : b));
        try {
            await updateBoardApi({ token, workspaceId, boardId, data });
        } catch (err) {
            console.error(err);
            // Revert or re-fetch
        }
    };

    const deleteBoard = async (boardId) => {
        setBoards(prev => prev.filter(b => b.id !== boardId));
        try {
            await deleteBoardApi({ token, workspaceId, boardId });
        } catch (err) {
            console.error(err);
        }
    };

    const fetchBoardTasks = async (boardId) => {
        // This might arguably belong in useTasks, but keeps board logic encapsulated here
        // The consumer (TaskListView) will need to decide whether to call fetchTasks(projectId) or fetchBoardTasks(boardId)
        try {
            return await getBoardTasksApi({ token, workspaceId, boardId });
        } catch (err) {
            console.error(err);
            return [];
        }
    };

    const fetchBoardStatuses = async (boardId) => {
        try {
            return await getBoardStatusesApi({ token, workspaceId, boardId });
        } catch (err) {
            console.error(err);
            return [];
        }
    };

    return {
        boards,
        loading,
        error,
        fetchBoards,
        createBoard,
        updateBoard,
        deleteBoard,
        fetchBoardTasks,
        fetchBoardStatuses
    };
}
